import type { DeckElement, DeckSlide, DeckTheme, SlideDeck } from '@/lib/slideDeck';
import { BG_PRESETS, THEME_PRESETS } from '@/lib/slideDeck';

interface SlideInspectorProps {
  deck: SlideDeck;
  slide: DeckSlide;
  selected: DeckElement | null;
  readOnly?: boolean;
  onUpdateElement: (id: string, patch: Partial<DeckElement>) => void;
  onUpdateSlide: (patch: Partial<DeckSlide>) => void;
  onUpdateDeck: (patch: Partial<SlideDeck>) => void;
  onLayer: (action: 'front' | 'forward' | 'backward' | 'back') => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onAlign: (dir: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom') => void;
}

const TEXT_COLORS = ['#1f2937', '#ffffff', '#0f766e', '#c2410c', '#a16207', '#9d174d', '#1e40af'];
const FILL_COLORS = ['#0f766e', '#c2410c', '#a16207', '#4d7c0f', '#334155', '#9d174d', '#ffffff', 'transparent'];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="block text-[10px] font-semibold uppercase tracking-wide text-foreground-400">{label}</label>
      {children}
    </div>
  );
}

function NumberInput({ value, onChange, suffix = '%' }: { value: number; onChange: (v: number) => void; suffix?: string }) {
  return (
    <div className="relative">
      <input
        type="number"
        value={Math.round(value * 10) / 10}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="w-full px-2 py-1.5 pr-6 rounded-md border border-background-200/70 bg-white text-xs text-foreground-800 focus:outline-none focus:border-primary-300"
      />
      <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-foreground-300">{suffix}</span>
    </div>
  );
}

export default function SlideInspector({
  deck,
  slide,
  selected,
  readOnly = false,
  onUpdateElement,
  onUpdateSlide,
  onUpdateDeck,
  onLayer,
  onDuplicate,
  onDelete,
  onAlign,
}: SlideInspectorProps) {
  const set = (patch: Partial<DeckElement>) => {
    if (!selected || readOnly) return;
    onUpdateElement(selected.id, patch);
  };

  return (
    <div className="w-64 flex-shrink-0 border-l border-background-200/70 bg-background-50 overflow-y-auto">
      {selected ? (
        <div className="p-3 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-foreground-800">
              {selected.type === 'text' ? 'Zone de texte' : selected.type === 'wordart' ? 'WordArt' : selected.type === 'shape' ? 'Forme' : selected.type === 'image' ? 'Image' : selected.type === 'chart' ? 'Graphique' : selected.type === 'table' ? 'Tableau' : selected.type === 'video' ? 'Vidéo' : 'Ligne'}
            </span>
            <span className="text-[10px] text-foreground-400">#{deck.slides.length ? '1' : '1'}</span>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Field label="X"><NumberInput value={selected.x} onChange={(v) => set({ x: v })} /></Field>
            <Field label="Y"><NumberInput value={selected.y} onChange={(v) => set({ y: v })} /></Field>
            <Field label="Largeur"><NumberInput value={selected.w} onChange={(v) => set({ w: Math.max(2, v) })} /></Field>
            <Field label="Hauteur"><NumberInput value={selected.h} onChange={(v) => set({ h: Math.max(2, v) })} /></Field>
          </div>

          <Field label={`Rotation (${selected.rotation || 0}°)`}>
            <input
              type="range"
              min={-180}
              max={180}
              value={selected.rotation || 0}
              onChange={(e) => set({ rotation: Number(e.target.value) })}
              className="w-full accent-primary-500 cursor-pointer"
            />
          </Field>

          <Field label="Opacité">
            <input
              type="range"
              min={10}
              max={100}
              value={Math.round((selected.opacity ?? 1) * 100)}
              onChange={(e) => set({ opacity: Number(e.target.value) / 100 })}
              className="w-full accent-primary-500 cursor-pointer"
            />
          </Field>

          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1.5">Alignement</p>
            <div className="grid grid-cols-3 gap-1">
              {[
                { dir: 'left', icon: 'ri-align-left' },
                { dir: 'center', icon: 'ri-align-center' },
                { dir: 'right', icon: 'ri-align-right' },
              ].map((a) => (
                <button
                  key={a.dir}
                  type="button"
                  onClick={() => set({ align: a.dir as DeckElement['align'] })}
                  className={`h-7 rounded-md flex items-center justify-center cursor-pointer ${selected.align === a.dir ? 'bg-accent-100 text-accent-700' : 'bg-white border border-background-200/70 text-foreground-500 hover:bg-background-100'}`}
                  title={`Aligner ${a.dir}`}
                >
                  <i className={a.icon}></i>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-1 mt-1">
              {(['top', 'middle', 'bottom'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => set({ valign: v })}
                  className={`h-7 rounded-md text-[10px] cursor-pointer ${selected.valign === v ? 'bg-accent-100 text-accent-700' : 'bg-white border border-background-200/70 text-foreground-500 hover:bg-background-100'}`}
                >
                  {v === 'top' ? 'Haut' : v === 'middle' ? 'Milieu' : 'Bas'}
                </button>
              ))}
            </div>
          </div>

          {['text', 'wordart', 'table', 'chart'].includes(selected.type) && (
            <>
              <Field label={`Taille du texte (${selected.fontSize ?? 3})`}>
                <input
                  type="range"
                  min={1.2}
                  max={12}
                  step={0.2}
                  value={selected.fontSize ?? 3}
                  onChange={(e) => set({ fontSize: Number(e.target.value) })}
                  className="w-full accent-primary-500 cursor-pointer"
                />
              </Field>
              <div className="flex items-center gap-1">
                {[
                  { key: 'bold', icon: 'ri-bold' },
                  { key: 'italic', icon: 'ri-italic' },
                  { key: 'underline', icon: 'ri-underline' },
                  { key: 'shadow', icon: 'ri-shadow-line' },
                ].map((b) => (
                  <button
                    key={b.key}
                    type="button"
                    onClick={() => set({ [b.key]: !(selected as unknown as Record<string, boolean>)[b.key] } as Partial<DeckElement>)}
                    className={`w-7 h-7 rounded-md flex items-center justify-center cursor-pointer ${
                      (selected as unknown as Record<string, boolean>)[b.key]
                        ? 'bg-accent-100 text-accent-700'
                        : 'bg-white border border-background-200/70 text-foreground-500 hover:bg-background-100'
                    }`}
                  >
                    <i className={b.icon}></i>
                  </button>
                ))}
              </div>
              <Field label="Couleur du texte">
                <div className="flex flex-wrap gap-1">
                  {TEXT_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => set({ color: c })}
                      className={`w-5 h-5 rounded-full border cursor-pointer ${selected.color === c ? 'border-primary-500 ring-1 ring-primary-300' : 'border-background-200'}`}
                      style={{ background: c }}
                    />
                  ))}
                </div>
              </Field>
            </>
          )}

          {['shape', 'chart', 'table', 'text', 'wordart'].includes(selected.type) && (
            <Field label="Couleur de remplissage">
              <div className="flex flex-wrap gap-1">
                {FILL_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => set({ fill: c === 'transparent' ? undefined : c })}
                    className="w-5 h-5 rounded border border-background-200 cursor-pointer"
                    style={{ background: c === 'transparent' ? 'repeating-conic-gradient(#e5e7eb 0% 25%, #ffffff 0% 50%) 50% / 6px 6px' : c }}
                    title={c}
                  />
                ))}
              </div>
            </Field>
          )}

          {selected.type === 'shape' && (
            <Field label="Forme">
              <select
                value={selected.shape || 'rect'}
                onChange={(e) => set({ shape: e.target.value as DeckElement['shape'] })}
                className="w-full px-2 py-1.5 rounded-md border border-background-200/70 bg-white text-xs text-foreground-800 cursor-pointer"
              >
                <option value="rect">Rectangle</option>
                <option value="roundRect">Rectangle arrondi</option>
                <option value="ellipse">Ellipse</option>
                <option value="triangle">Triangle</option>
                <option value="diamond">Losange</option>
                <option value="arrow">Flèche</option>
              </select>
            </Field>
          )}

          {selected.type === 'image' && (
            <Field label="URL de l'image">
              <input
                value={selected.src || ''}
                onChange={(e) => set({ src: e.target.value })}
                className="w-full px-2 py-1.5 rounded-md border border-background-200/70 bg-white text-[11px] text-foreground-700"
                placeholder="https://…"
              />
            </Field>
          )}

          {selected.type === 'video' && (
            <Field label="Lien vidéo (YouTube ou direct)">
              <input
                value={selected.videoUrl || ''}
                onChange={(e) => set({ videoUrl: e.target.value })}
                className="w-full px-2 py-1.5 rounded-md border border-background-200/70 bg-white text-[11px] text-foreground-700"
                placeholder="https://youtube.com/watch?v=…"
              />
            </Field>
          )}

          <Field label="Lien au clic">
            <input
              value={selected.link || ''}
              onChange={(e) => set({ link: e.target.value })}
              className="w-full px-2 py-1.5 rounded-md border border-background-200/70 bg-white text-[11px] text-foreground-700"
              placeholder="https://…"
            />
          </Field>

          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1.5">Ordre (axe Z)</p>
            <div className="grid grid-cols-2 gap-1">
              {[
                { a: 'front', label: 'Premier plan', icon: 'ri-bring-to-front' },
                { a: 'forward', label: 'Avancer', icon: 'ri-arrow-up-line' },
                { a: 'backward', label: 'Reculer', icon: 'ri-arrow-down-line' },
                { a: 'back', label: 'Arrière-plan', icon: 'ri-send-to-back' },
              ].map((b) => (
                <button
                  key={b.a}
                  type="button"
                  onClick={() => onLayer(b.a as 'front' | 'forward' | 'backward' | 'back')}
                  className="h-7 rounded-md bg-white border border-background-200/70 text-[10px] text-foreground-600 hover:bg-background-100 cursor-pointer whitespace-nowrap"
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1.5">Aligner sur la diapositive</p>
            <div className="grid grid-cols-3 gap-1">
              {([
                { dir: 'left', icon: 'ri-align-left' },
                { dir: 'center', icon: 'ri-align-center' },
                { dir: 'right', icon: 'ri-align-right' },
                { dir: 'top', icon: 'ri-align-top' },
                { dir: 'middle', icon: 'ri-align-vertically' },
                { dir: 'bottom', icon: 'ri-align-bottom' },
              ] as const).map((d) => (
                <button
                  key={d.dir}
                  type="button"
                  onClick={() => onAlign(d.dir)}
                  className="h-7 rounded-md bg-white border border-background-200/70 text-foreground-500 hover:bg-background-100 cursor-pointer"
                  title={d.dir}
                >
                  <i className={d.icon}></i>
                </button>
              ))}
            </div>
          </div>

          <Field label="Animation à l'apparition">
            <select
              value={selected.anim || 'none'}
              onChange={(e) => set({ anim: e.target.value as DeckElement['anim'] })}
              className="w-full px-2 py-1.5 rounded-md border border-background-200/70 bg-white text-xs text-foreground-800 cursor-pointer"
            >
              <option value="none">Aucune</option>
              <option value="fade">Fondu</option>
              <option value="appear">Apparition</option>
              <option value="zoom">Zoom</option>
              <option value="rotate">Rotation</option>
            </select>
          </Field>

          <div className="flex items-center gap-1 pt-1">
            <button type="button" onClick={onDuplicate} className="flex-1 h-8 rounded-md bg-white border border-background-200/70 text-[11px] text-foreground-600 hover:bg-background-100 cursor-pointer whitespace-nowrap">
              <i className="ri-file-copy-line mr-1"></i>Dupliquer
            </button>
            <button type="button" onClick={onDelete} className="flex-1 h-8 rounded-md bg-white border border-red-200 text-[11px] text-red-600 hover:bg-red-50 cursor-pointer whitespace-nowrap">
              <i className="ri-delete-bin-line mr-1"></i>Supprimer
            </button>
          </div>
        </div>
      ) : (
        <div className="p-3 space-y-4">
          <div>
            <p className="text-xs font-semibold text-foreground-800 mb-2">Diapositive</p>
            <Field label="Titre interne">
              <input
                value={slide.title}
                onChange={(e) => onUpdateSlide({ title: e.target.value })}
                className="w-full px-2 py-1.5 rounded-md border border-background-200/70 bg-white text-xs text-foreground-800"
              />
            </Field>
          </div>
          <Field label="Couleur de fond">
            <div className="flex flex-wrap gap-1">
              {BG_PRESETS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => onUpdateSlide({ bg: c })}
                  className={`w-6 h-6 rounded border cursor-pointer ${slide.bg === c ? 'border-primary-500 ring-1 ring-primary-300' : 'border-background-200'}`}
                  style={{ background: c }}
                />
              ))}
            </div>
          </Field>
          <Field label="Image de fond (URL)">
            <input
              value={slide.bgImage || ''}
              onChange={(e) => onUpdateSlide({ bgImage: e.target.value })}
              placeholder="https://…"
              className="w-full px-2 py-1.5 rounded-md border border-background-200/70 bg-white text-[11px] text-foreground-700"
            />
          </Field>
          <Field label="Transition">
            <select
              value={slide.transition || 'fade'}
              onChange={(e) => onUpdateSlide({ transition: e.target.value as DeckSlide['transition'] })}
              className="w-full px-2 py-1.5 rounded-md border border-background-200/70 bg-white text-xs text-foreground-800 cursor-pointer"
            >
              <option value="none">Aucune</option>
              <option value="fade">Fondu</option>
              <option value="slide">Glissement</option>
              <option value="zoom">Zoom</option>
              <option value="dissolve">Dissolution</option>
            </select>
          </Field>
          <Field label="Notes du présentateur">
            <textarea
              value={slide.notes || ''}
              onChange={(e) => onUpdateSlide({ notes: e.target.value })}
              rows={4}
              maxLength={500}
              placeholder="Ce que vous direz pendant cette diapositive…"
              className="w-full px-2 py-1.5 rounded-md border border-background-200/70 bg-white text-[11px] text-foreground-700 resize-none"
            />
          </Field>

          <div className="pt-2 border-t border-background-200/70 space-y-3">
            <p className="text-xs font-semibold text-foreground-800">Thème global</p>
            <div className="grid grid-cols-2 gap-1">
              {THEME_PRESETS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onUpdateDeck({ theme: t.theme })}
                  className={`rounded-md border p-1.5 text-left cursor-pointer ${deck.theme.primary === t.theme.primary ? 'border-primary-400 bg-primary-50' : 'border-background-200/70 bg-white hover:bg-background-100'}`}
                >
                  <span className="flex gap-1 mb-1">
                    <span className="w-3 h-3 rounded-sm" style={{ background: t.theme.primary }} />
                    <span className="w-3 h-3 rounded-sm" style={{ background: t.theme.accent }} />
                    <span className="w-3 h-3 rounded-sm border border-background-200" style={{ background: t.theme.background }} />
                  </span>
                  <span className="text-[10px] text-foreground-600">{t.label}</span>
                </button>
              ))}
            </div>
            <Field label="Format de la page">
              <select
                value={`${deck.w}x${deck.h}`}
                onChange={(e) => {
                  const [w, h] = e.target.value.split('x').map(Number);
                  onUpdateDeck({ w, h });
                }}
                className="w-full px-2 py-1.5 rounded-md border border-background-200/70 bg-white text-xs text-foreground-800 cursor-pointer"
              >
                <option value="960x540">16:9 — 960 × 540</option>
                <option value="1024x768">4:3 — 1024 × 768</option>
                <option value="1080x1080">Carré — 1080 × 1080</option>
                <option value="720x1280">Vertical — 720 × 1280</option>
              </select>
            </Field>
          </div>
        </div>
      )}
    </div>
  );
}